import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("elm_admin_token") || ""}` }, withCredentials: true });
export const inputCls = "w-full bg-white/70 border border-wood/25 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-terra";
export const btn = "px-3 py-1.5 rounded-full border border-wood/25 text-xs text-bark hover:border-terra hover:text-terra transition-colors disabled:opacity-40 inline-flex items-center gap-1";
export const btnPrimary = "px-4 py-2 rounded-full bg-earth text-sand text-xs hover:bg-terra hover:text-earth transition-colors disabled:opacity-50 inline-flex items-center gap-1.5";
export const d = (iso) => (iso ? new Intl.DateTimeFormat("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(iso.length > 10 ? iso : `${iso}T12:00:00`)) : "—");
export const dt = (iso) => (iso ? new Intl.DateTimeFormat("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(iso)) : "—");
export const errMsg = (e) => { const x = e.response?.data?.detail; return typeof x === "string" ? x : Array.isArray(x) ? x.map((i) => i.msg).join(" ") : "Erreur"; };

const TONES = { ok: "bg-sage/15 text-sage", warn: "bg-terra/15 text-terra", neutral: "bg-sand-dark text-fumee", bad: "bg-destructive/10 text-destructive" };
export const Badge = ({ children, tone = "neutral", testid }) => <span data-testid={testid} className={`inline-block font-mono text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full whitespace-nowrap ${TONES[tone]}`}>{children}</span>;
export const statutTone = (s) => (s === "confirmée" ? "ok" : s === "acompte versé" ? "warn" : s === "en attente de paiement" ? "neutral" : "bad");
export const payTone = (code) => ({ paye: "ok", gratuit: "neutral", annule: "neutral", attente: "warn", solde: "warn", facture: "warn" }[code] || "neutral");

export const PageHeader = ({ title, actions, testid }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 mb-6" data-testid={testid}>
    <h2 className="font-serif text-2xl sm:text-3xl text-bark">{title}</h2>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export const Kpi = ({ label, value, testid, onClick }) => (
  <button type="button" onClick={onClick} disabled={!onClick} className="text-left bg-card border border-sand-dark rounded-xl p-4 hover:border-terra transition-colors disabled:hover:border-sand-dark" data-testid={testid}>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fumee">{label}</p>
    <p className="font-serif text-2xl sm:text-3xl text-bark mt-1.5">{value}</p>
  </button>
);

export const Table = ({ cols, rows, empty, testid, onRow }) => (
  <div className="overflow-x-auto bg-card border border-sand-dark rounded-xl" data-testid={testid}>
    <table className="w-full text-sm min-w-[640px]">
      <thead><tr className="border-b border-sand-dark">{cols.map((c) => <th key={c} className="text-left font-mono text-[10px] uppercase tracking-[0.18em] text-fumee px-3 py-2.5 whitespace-nowrap">{c}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-fumee">{empty}</td></tr>}
        {rows.map((r, i) => <tr key={i} onClick={onRow ? () => onRow(i) : undefined} className={`border-b border-sand-dark/60 last:border-0 align-top ${onRow ? "cursor-pointer hover:bg-sand/50" : ""}`}>{r.map((cell, j) => <td key={j} className="px-3 py-2.5 text-bark">{cell}</td>)}</tr>)}
      </tbody>
    </table>
  </div>
);

export const Pager = ({ page, pages, total, onPage, testid = "admin-pager" }) => (
  <div className="flex items-center justify-between gap-3 mt-3 text-xs text-fumee" data-testid={testid}>
    <span data-testid={`${testid}-total`}>{total} résultat{total > 1 ? "s" : ""}</span>
    <span className="flex items-center gap-2">
      <button type="button" className={btn} disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testid}-prev`}><ChevronLeft className="w-3 h-3" /></button>
      <span data-testid={`${testid}-page`}>Page {page} / {pages}</span>
      <button type="button" className={btn} disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${testid}-next`}><ChevronRight className="w-3 h-3" /></button>
    </span>
  </div>
);

export const Loading = () => <div className="py-12 text-center"><Loader2 className="w-6 h-6 text-terra animate-spin mx-auto" /></div>;

export const useAdminGet = (path, params) => {
  const [data, setData] = useState(null);
  const key = JSON.stringify(params || {});
  const reload = useCallback(() => axios.get(`${API}${path}`, { ...authHeaders(), params: JSON.parse(key) }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [path, key]);
  useEffect(() => { reload(); }, [reload]);
  return [data, reload];
};
